import { App, Divider } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  FormOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  useDeleteDataBlockMutation,
  useLazyGetDataBlockByIdQuery,
  useLazyGetDataTemplateByIdQuery,
  useLazyIncrementDataBlockViewsQuery,
  usePatchDataBlockMutation,
} from "@/redux/service";
import UserAvatar from "@/domains/users/components/UserAvatar";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseTag from "@/components_v2/Tags/BaseTag";
import AddContributorsModal from "@/features/datablocks/core/components/AddContributorsModal";
import { getContentItemForField } from "@/domains/datablocks/fields/renderers";
import { useDataBlockPermissions } from "@/features/datablocks/core/hooks/useDataBlockPermissions";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import DataBlockReferences from "@/features/datablocks/core/components/DataBlockReferences";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import {
  DataBlockActionButtonsProps,
  DataBlockSidebarProps,
} from "@/features/datablocks/types/types";
import {
  DataBlockContent,
  DataBlockDetails,
} from "@/features/datablocks/core/components/DataBlockContent";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import { OrganizationRoles } from "@/shared/utils/constants";
import EditRequestModal from "@/domains/evidence-requests/components/EditRequestModal";
import { RequestStatus } from "@/domains/evidence-requests/constants";
import {
  DataBlockResponse,
  FieldValueResponse,
} from "@/redux/datablocks/types";
import { useEvidenceContributors } from "@/domains/evidence/hooks/useEvidenceFields";
import { BaseUserResponse } from "@/redux/users/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { DataBlockRequestEditModalsState } from "@/features/evidence-requests/list/types";

const DataBlockSidebar = ({
  dataBlock,
  dataTemplate,
  showCtas,
}: DataBlockSidebarProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [patchDataBlock] = usePatchDataBlockMutation();
  const [contributorModalOpen, setContributorModalOpen] = useState(false);
  const contributors = useEvidenceContributors(dataBlock);
  const currentUser = useSelector(selectCurrentUser);
  const { canEdit } = useDataBlockPermissions({
    dataBlock,
    dataTemplate,
    currentUser,
  });

  const deleteContributor = (deletedContributorId: number) => {
    const data = {
      fieldValues: [
        {
          field: dataBlock.fieldsMapping.contributors,
          value: (contributors.data as BaseUserResponse[])
            .filter((i) => i.id !== deletedContributorId)
            .map((i) => i.id),
        },
      ],
    };
    patchDataBlock({ id: dataBlock.id, data })
      .unwrap()
      .then(() =>
        notification.success({
          message: t("datablocks.labels.contributorsDeleted"),
        }),
      );
  };

  return (
    <div className="data-block-sidebar">
      <div className="data-block-sidebar-contributors">
        <div className="data-block-sidebar-contributors-header">
          <h2 className="header-m-medium">
            {t("datablocks.labels.contributors")}
          </h2>

          {canEdit && showCtas && (
            <BaseButton
              variant={BUTTON_VARIANTS.LINK}
              size={BUTTON_SIZES.SMALL}
              leadingIcon={<PlusOutlined />}
              onClick={() => setContributorModalOpen(true)}
            >
              {t("datablocks.labels.addContributors")}
            </BaseButton>
          )}
        </div>

        <div className="data-block-sidebar-contributors-items-container">
          {contributors.isLoading ? (
            <div className="data-block-sidebar-contributors-items-container">
              <BaseSpinner size="small" />
            </div>
          ) : (
            (contributors.data as BaseUserResponse[]).map((contributor) => (
              <div
                key={contributor.id}
                className="data-block-sidebar-contributors-items"
              >
                <div className="data-block-sidebar-contributors-items-details">
                  <UserAvatar user={contributor} />
                  <span className="data-block-sidebar-contributors-items-names">
                    {contributor.fullName}
                  </span>
                </div>

                {canEdit && showCtas && (
                  <DeleteOutlined
                    onClick={() => deleteContributor(contributor.id)}
                  />
                )}
              </div>
            ))
          )}
        </div>
      </div>
      <Divider />
      <DataBlockReferences dataBlock={dataBlock} />
      <AddContributorsModal
        open={contributorModalOpen}
        setOpen={setContributorModalOpen}
        dataBlock={dataBlock}
        currentContributorIds={
          contributors.isLoading
            ? []
            : (contributors.data as BaseUserResponse[]).map((i) => i.id)
        }
      />
    </div>
  );
};

const DataBlockActionButtons = ({
  dataBlock,
  dataTemplate,
  setOpenDeleteModal,
  setEditRequestModal,
}: DataBlockActionButtonsProps) => {
  const { t } = useTranslation();
  const { dataBlockId } = useParams();

  const currentUser = useSelector(selectCurrentUser);
  const { canEdit, canDelete, isOwner, isContributor } =
    useDataBlockPermissions({ dataBlock, dataTemplate, currentUser });

  const deleteTooltipErrorMessage = useMemo(() => {
    if (!dataBlock || canDelete) return;

    if (dataBlock.isUsedInActiveSurvey) {
      return t("datablocks.labels.notDeletableUsedInSurvey");
    }

    if (dataBlock.request) {
      return t("datablocks.labels.notDeletableByInvitee");
    }
  }, [canDelete, dataBlock]);

  const editTooltipErrorMessage = useMemo(() => {
    if (canEdit) return;

    return t("datablocks.labels.notEditableByInvitee");
  }, [canEdit]);

  const editDeleteButtons = useMemo(() => {
    if (!dataBlock || (!isOwner && !isContributor)) return;

    return (
      <>
        <BaseTooltip title={deleteTooltipErrorMessage} placement="topLeft">
          <BaseButton
            variant={BUTTON_VARIANTS.TERTIARY}
            leadingIcon={<DeleteOutlined />}
            disabled={!canDelete}
            size={BUTTON_SIZES.SMALL}
            onClick={() => setOpenDeleteModal(true)}
          >
            {t("datablocks.labels.deleteDatablock")}
          </BaseButton>
        </BaseTooltip>
        <BaseTooltip title={editTooltipErrorMessage} placement="topLeft">
          <BaseButton
            variant={BUTTON_VARIANTS.SECONDARY}
            leadingIcon={<EditOutlined />}
            disabled={!canEdit}
            size={BUTTON_SIZES.SMALL}
            type={BUTTON_TYPES.LINK}
            to={`/evidence/${dataBlockId}/edit`}
          >
            {t("datablocks.labels.editDatablock")}
          </BaseButton>
        </BaseTooltip>
      </>
    );
  }, [dataBlock, isOwner, isContributor, canEdit, canDelete, dataBlockId]);

  const requestEditButton = useMemo(() => {
    if (
      !currentUser ||
      !currentUser.organization ||
      !dataBlock ||
      dataBlock.request == null
    )
      return null;

    const isCurrentUserAdminOrSupervisor =
      currentUser.organizationRole &&
      [
        OrganizationRoles.ADMIN.value,
        OrganizationRoles.SUPERVISOR.value,
      ].includes(currentUser.organizationRole);
    const dataBlockBelongsToCurrentOrg =
      currentUser.organization?.id === dataBlock.organization.id;

    if (!isCurrentUserAdminOrSupervisor || !dataBlockBelongsToCurrentOrg)
      return;
    const isEditAlreadyRequested =
      dataBlock.request!.status === RequestStatus.REVISION_NEEDED.value;

    return (
      <BaseTooltip
        title={
          isEditAlreadyRequested
            ? t("datablocks.labels.editIsAlreadyRequested")
            : undefined
        }
        placement="topLeft"
      >
        <BaseButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SECONDARY}
          leadingIcon={<FormOutlined />}
          disabled={isEditAlreadyRequested}
          onClick={() =>
            setEditRequestModal({
              open: true,
              request: dataBlock.request!.id,
            })
          }
        >
          {t("datablocks.labels.requestEdit")}
        </BaseButton>
      </BaseTooltip>
    );
  }, [currentUser, dataBlock]);

  return dataBlock.editable ? (
    <>
      {editDeleteButtons}
      {requestEditButton}
    </>
  ) : null;
};

const DataBlockContainer = ({
  initialDataBlock,
  showCtas = true,
}: {
  initialDataBlock: DataBlockResponse;
  showCtas: boolean;
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { dataBlockId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [getDataBlockById, { data: fetchedDataBlock }] =
    useLazyGetDataBlockByIdQuery();
  const [getDataTemplateById, { data: dataTemplate }] =
    useLazyGetDataTemplateByIdQuery();
  const currentUser = useSelector(selectCurrentUser);
  const [deleteDataBlock] = useDeleteDataBlockMutation();
  const [incrementDataBlockViews] = useLazyIncrementDataBlockViewsQuery();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const dataBlock = useMemo(
    () => initialDataBlock || fetchedDataBlock,
    [initialDataBlock, fetchedDataBlock],
  );
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [editRequestModal, setEditRequestModal] =
    useState<DataBlockRequestEditModalsState>({
      open: false,
      request: undefined,
    });
  const { canView } = useDataBlockPermissions({
    dataBlock,
    dataTemplate,
    currentUser,
  });

  const titleFieldValue = useMemo(() => {
    if (!dataTemplate || !dataBlock) return;
    const field = dataTemplate.templateFields.find(
      (tf) => tf.id === dataBlock.fieldsMapping.title,
    );
    const value = dataBlock.fieldValues.find(
      (fv: FieldValueResponse) => fv.field === dataBlock.fieldsMapping.title,
    )?.value;
    if (!field || !value) return;

    return {
      field,
      value,
    };
  }, [dataTemplate, dataBlock]);

  useEffect(() => {
    if (dataTemplate && currentUser && dataBlock) {
      if (!canView) {
        notification.error({
          message: t("datablocks.errors.unauthorized"),
        });
        navigate(`/evidence`);
      }
      setPermissionChecked(true);
    }
  }, [dataTemplate, currentUser, dataBlock]);

  useEffect(() => {
    if (dataBlockId) {
      getDataBlockById(dataBlockId);
    }
  }, [dataBlockId]);

  useEffect(() => {
    if (dataBlock) {
      getDataTemplateById(dataBlock.template.id);
      setTimeout(() => {
        incrementDataBlockViews(dataBlock.id);
      }, 10000);
    }
  }, [dataBlock]);

  const handleDelete = () => {
    deleteDataBlock(dataBlockId)
      .unwrap()
      .then(() => {
        notification.success({
          message: t("datablocks.labels.datablockIsDeleted"),
        });
        navigate("/evidence");
      });
  };

  const calculateTotalHeight = () => {
    const headerButton = document.querySelector(
      ".data-block-header-button",
    ) as HTMLElement;
    const headerBlock = document.querySelector(
      ".data-block-header",
    ) as HTMLElement;
    const baseHeader = document.querySelector(".base-header") as HTMLElement;

    const height =
      (headerButton?.offsetHeight || 0) +
      (headerBlock?.offsetHeight || 0) +
      (baseHeader?.offsetHeight || 0);

    return height + 1000 + "px";
  };

  if (!dataBlock || !dataTemplate || !permissionChecked) {
    return <BaseSpinner />;
  }

  return (
    <div className="data-block">
      {showCtas &&
      currentUser &&
      currentUser.organizationRole !== OrganizationRoles.GUEST.value ? (
        <div className="data-block-header-button">
          {location.state?.showBackButton !== false ? (
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.TERTIARY}
              type={BUTTON_TYPES.LINK}
              to={-1}
            >
              {t("datablocks.labels.back")}
            </BaseButton>
          ) : (
            <div />
          )}
          <BaseButton
            size="small"
            to={"/discussions/form"}
            type={BUTTON_TYPES.LINK}
            state={{ dataBlock }}
          >
            {t("datablocks.labels.startDiscussion")}
          </BaseButton>
        </div>
      ) : null}

      <div className="data-block-header">
        <div className="data-block-header-content">
          <BaseTag color={dataTemplate.color} label={dataTemplate.title} />
          {titleFieldValue &&
            getContentItemForField(titleFieldValue, { label: undefined })}
        </div>

        <div className="data-block-header-content">
          {showCtas ? (
            <DataBlockActionButtons
              dataBlock={dataBlock}
              dataTemplate={dataTemplate}
              setOpenDeleteModal={setOpenDeleteModal}
              setEditRequestModal={setEditRequestModal}
            />
          ) : null}
        </div>
      </div>

      <div
        className="data-block-details-main-content-container"
        style={{
          height: `calc(100vh - ${calculateTotalHeight})`,
        }}
      >
        <div className="data-block-details-main-content-left-container">
          <DataBlockDetails dataBlock={dataBlock} dataTemplate={dataTemplate} />
          <DataBlockContent dataBlock={dataBlock} dataTemplate={dataTemplate} />
        </div>

        <div className="data-block-details-main-content-right-container">
          <DataBlockSidebar
            dataBlock={dataBlock}
            dataTemplate={dataTemplate}
            showCtas={showCtas}
          />
        </div>
      </div>

      <CancelConfirmationModal
        title={t("confirm.confirmDelete")}
        content={
          <span className="body-m-regular">
            {t("datablocks.labels.confirmDeleteContent")}{" "}
            <span className="body-m-medium">
              "{titleFieldValue?.value as string | undefined}"
            </span>
            ?
          </span>
        }
        confirmButtonText={t("buttons.delete")}
        open={openDeleteModal}
        onConfirm={handleDelete}
        onCancel={() => setOpenDeleteModal(false)}
      />

      <EditRequestModal
        open={editRequestModal.open}
        setOpen={(value) =>
          setEditRequestModal({ open: value, request: undefined })
        }
        request={editRequestModal.request}
      />
    </div>
  );
};

export default DataBlockContainer;
